import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Instagram as InstagramIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { clientApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, NicheChip, EmptyState } from '../components/common';
import {
  formatCurrency,
  formatDate,
  getInstagramProfileUrl,
  getInitials,
  debounce,
} from '../utils/helpers';

const statusColors = {
  active: 'success',
  paused: 'warning',
  cancelled: 'error',
};

const Clients = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useApp();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 0, limit: 24, total: 0 });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        search: search || undefined,
        status: statusFilter || undefined,
      };

      const response = await clientApi.getAll(params);
      setClients(response.data.clients);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      showSnackbar('Failed to load clients', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter, showSnackbar]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
  };

  const renderClientCard = (client) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={client._id}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
        onClick={() => navigate(`/clients/${client._id}`)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.main',
                fontSize: '1rem',
              }}
            >
              {getInitials(client.businessName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {client.businessName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {client.ownerName}
              </Typography>
            </Box>
            <Chip
              label={client.status}
              size="small"
              color={statusColors[client.status]}
              sx={{ fontWeight: 600, textTransform: 'capitalize' }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Monthly Rate
              </Typography>
              <Typography variant="body2" fontWeight={600} color="success.main">
                {formatCurrency(client.monthlyRate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Client Since
              </Typography>
              <Typography variant="body2">
                {formatDate(client.billingStartDate, 'MMM yyyy')}
              </Typography>
            </Box>
            {client.niche && (
              <Box sx={{ mt: 1 }}>
                <NicheChip niche={client.niche} />
              </Box>
            )}
          </Box>
        </CardContent>
        <CardActions sx={{ px: 2, pb: 2 }}>
          {client.websiteUrl && (
            <Button
              size="small"
              startIcon={<LaunchIcon />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(client.websiteUrl, '_blank');
              }}
            >
              Website
            </Button>
          )}
          {client.instagramHandle && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(getInstagramProfileUrl(client.instagramHandle), '_blank');
              }}
            >
              <InstagramIcon fontSize="small" />
            </IconButton>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  const renderClientTable = () => (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Business</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Niche</TableCell>
              <TableCell align="right">Monthly Rate</TableCell>
              <TableCell>Client Since</TableCell>
              <TableCell>Months</TableCell>
              <TableCell align="right">LTV</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow
                key={client._id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/clients/${client._id}`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {getInitials(client.businessName)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>
                      {client.businessName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{client.ownerName || '-'}</TableCell>
                <TableCell>
                  {client.niche && <NicheChip niche={client.niche} />}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {formatCurrency(client.monthlyRate)}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(client.billingStartDate, 'MMM d, yyyy')}</TableCell>
                <TableCell>{client.monthsActive || 0}</TableCell>
                <TableCell align="right">
                  {formatCurrency(client.lifetimeValue || 0)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={client.status}
                    size="small"
                    color={statusColors[client.status]}
                    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page}
        onPageChange={handlePageChange}
        rowsPerPage={pagination.limit}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[12, 24, 48]}
      />
    </Paper>
  );

  return (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search clients..."
            size="small"
            onChange={handleSearchChange}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 150 }}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="grid">
              <GridIcon />
            </ToggleButton>
            <ToggleButton value="list">
              <ListIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/leads?stage=won')}
          >
            Convert Lead
          </Button>
        </Box>
      </Paper>

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading clients..." />
      ) : clients.length === 0 ? (
        <EmptyState
          title="No clients found"
          description="Convert your first lead to a client to see them here"
          actionLabel="View Leads"
          onAction={() => navigate('/leads')}
        />
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {clients.map(renderClientCard)}
        </Grid>
      ) : (
        renderClientTable()
      )}
    </Box>
  );
};

export default Clients;
